// One customer's activity: the year-to-date and lifetime metrics above the
// full history, newest first. Reached from the activity dashboard; the
// franchisee filter scopes both the metrics and the table.
//
// Every figure on this page and everywhere else computes live from
// activity_records, so the admin-only Remove on a line moves the metrics,
// the dashboard, the profile rollup and the reports at once.

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getCustomerActivity, removeActivityRecord } from '../api.js'
import { useAuth } from '../auth.jsx'
import { ACTION } from '../../../shared/permissions.js'
import { useConfirm } from '../confirm.jsx'
import {
  Button,
  ButtonLink,
  EmptyState,
  Kpi,
  KpiRow,
  Notice,
  Panel,
  Pill,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { customerStatusTone } from '../tones/customers.js'
import { activitySourceTone } from '../tones/activity.js'
import {
  statusLabel,
  formatMoney,
  formatCount,
  formatDate,
  formatTenure,
  monthLabel,
  activitySourceLabel,
} from '../vocab.js'

export default function ActivityCustomer() {
  const { id } = useParams()
  const { canView, can } = useAuth()
  const canDelete = can(ACTION.RECORDS_DELETE)
  const mine = !can(ACTION.ACTIVITY_READ_ALL)
  const confirm = useConfirm()
  const [franchiseeId, setFranchiseeId] = useState('')
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [actionError, setActionError] = useState(null)

  const load = useCallback(async () => {
    try {
      setData(await getCustomerActivity(id, { franchiseeId }))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [id, franchiseeId])

  useEffect(() => {
    load()
  }, [load])

  async function drop(record) {
    const what = [record.description, record.amount ? formatMoney(record.amount) : null]
      .filter(Boolean)
      .join(', ')
    if (
      !(await confirm(
        `Remove ${what} from ${monthLabel(record.serviceMonth)}? Every total recomputes without it.`,
        { title: 'Remove the line', confirmLabel: 'Remove line' },
      ))
    ) {
      return
    }
    setActionError(null)
    try {
      await removeActivityRecord(record.id)
      load()
    } catch (caught) {
      setActionError(caught.message)
    }
  }

  if (loadError) {
    return (
      <Notice tone="bad">
        {loadError} <Link to="/activity">Back to activity</Link>.
      </Notice>
    )
  }
  if (!data) return <div className="center-note">Loading</div>

  const { customer, metrics, records } = data
  const bills = metrics.ftfs ?? null

  return (
    <>
      <header className="page-head">
        <h1>{customer.name}</h1>
        <p className="support">
          <Pill tone={customerStatusTone[customer.status]}>{statusLabel(customer.status)}</Pill>{' '}
          {customer.customerSince
            ? `Customer for ${formatTenure(customer.customerSince)}`
            : 'Customer start date not set'}
          {customer.createdByImport ? ' · Created by import' : ''}
        </p>
      </header>

      <div className="toolbar">
        <ButtonLink to="/activity">{mine ? 'Back to My Activity' : 'Back to activity'}</ButtonLink>
        {canView('customers') ? (
          <ButtonLink to={`/customers/${customer.id}`}>Customer record</ButtonLink>
        ) : null}
        <span className="spacer" />
        <Select
          value={franchiseeId}
          onChange={setFranchiseeId}
          ariaLabel="Filter by franchisee"
          style={{ width: '16rem' }}
          options={[
            { value: '', label: 'All franchisees' },
            ...data.franchisees.map((franchisee) => ({
              value: franchisee.id,
              label: franchisee.name,
            })),
          ]}
        />
        {franchiseeId ? (
          <Button variant="ghost" onClick={() => setFranchiseeId('')}>
            Clear
          </Button>
        ) : null}
      </div>

      {actionError ? <Notice tone="bad">{actionError}</Notice> : null}

      <KpiRow>
        <Kpi
          lead
          label={
            mine ? 'Your revenue this year' : bills ? 'Owner revenue this year' : 'Spend this year'
          }
          value={formatMoney(metrics.ytdSpend)}
        />
        <Kpi label="Tests this year" value={formatCount(metrics.ytdTests) ?? '0'} />
        <Kpi
          label={mine ? 'Lifetime revenue' : 'Lifetime spend'}
          value={formatMoney(metrics.lifetimeSpend)}
        />
        <Kpi label="Lifetime tests" value={formatCount(metrics.lifetimeTests) ?? '0'} />
        <Kpi
          label={mine ? 'Your clinics on it' : 'Franchisees served'}
          value={formatCount(metrics.franchiseeCount) ?? '0'}
        />
      </KpiRow>

      {bills ? (
        <KpiRow>
          <Kpi lead label="FTFS revenue this year" value={formatMoney(bills.ytdSpend)} />
          <Kpi label="FTFS tests this year" value={formatCount(bills.ytdTests) ?? '0'} />
          <Kpi label="FTFS lifetime revenue" value={formatMoney(bills.lifetimeSpend)} />
          <Kpi label="FTFS lifetime tests" value={formatCount(bills.lifetimeTests) ?? '0'} />
        </KpiRow>
      ) : null}

      <Panel
        title="History"
        support={
          records.length
            ? `${records.length} lines, newest first${
                metrics.firstActivityMonth
                  ? `, since ${monthLabel(metrics.firstActivityMonth)}`
                  : ''
              }`
            : 'Nothing recorded yet'
        }
        flush
      >
        {records.length === 0 ? (
          <EmptyState title="No work recorded">
            {franchiseeId
              ? 'No work by that franchisee for this customer. Clear the filter to see everything.'
              : 'Lines appear here when a monthly import includes this customer.'}
          </EmptyState>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Month</Th>
                {bills ? <Th>Source</Th> : null}
                <Th>Date</Th>
                <Th>Work</Th>
                <Th numeric>Quantity</Th>
                <Th numeric>Amount</Th>
                <Th>Performed by</Th>
                {canDelete ? <Th aria-label="Actions" /> : null}
              </Tr>
            </Thead>
            <Tbody>
              {records.map((record) => (
                <Tr key={record.id}>
                  <Td>{monthLabel(record.serviceMonth)}</Td>
                  {bills ? (
                    <Td>
                      <Pill tone={activitySourceTone[record.source]}>
                        {activitySourceLabel(record.source)}
                      </Pill>
                    </Td>
                  ) : null}
                  <Td>
                    {record.serviceDate ? (
                      formatDate(record.serviceDate)
                    ) : (
                      <span className="muted">Not stated</span>
                    )}
                  </Td>
                  <Td className="wraps">
                    {record.description}
                    {record.sourceFileName ? (
                      <div className="ui-cell-meta">{record.sourceFileName}</div>
                    ) : null}
                  </Td>
                  <Td numeric>{record.quantity ?? ''}</Td>
                  <Td numeric>
                    {record.amount === null ? (
                      <span className="muted">Not stated</span>
                    ) : (
                      formatMoney(record.amount)
                    )}
                  </Td>
                  <Td>{record.franchiseeName ?? <span className="muted">Not attributed</span>}</Td>
                  {canDelete ? (
                    <Td numeric>
                      <Button variant="ghost" onClick={() => drop(record)}>
                        Remove
                      </Button>
                    </Td>
                  ) : null}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Panel>
    </>
  )
}
